import {
  Alert, Button, Card, Descriptions, Empty, Input, Modal, Space, Spin, Typography,
} from 'antd';
import rentalApi from 'api/rentalApi';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { formatDate, formatDateTime, getRentalErrorMessage, LicenseStatusTag } from '../../rentalConstants';

const { Text, Title } = Typography;

function RentalRenterLicenseDetailScreen() {
  const router = useRouter();
  const rentalRenterLicenseId = router.query?.rentalRenterLicenseId;
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = String(user?.role || '').toUpperCase() === 'ADMIN';
  const canReview = isAdmin || permissionList?.some((permission) => permission?.code === 'RENTAL_LICENSE_REVIEW');

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    if (!rentalRenterLicenseId) return;
    setLoading(true);
    try {
      const response = await rentalApi.getRenterLicense(rentalRenterLicenseId);
      setDetail(response?.license || null);
    } catch (error) {
      setDetail(null);
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [rentalRenterLicenseId]);

  useEffect(() => { load(); }, [load]);

  const approve = () => Modal.confirm({
    title: 'Duyệt GPLX khách thuê?',
    content: 'Khách hàng sẽ được phép đặt xe sau khi duyệt.',
    okText: 'Duyệt',
    cancelText: 'Huỷ',
    onOk: async () => {
      setActionLoading(true);
      try {
        const response = await rentalApi.approveRenterLicense(rentalRenterLicenseId);
        setDetail(response?.license || null);
        ToastHelper.showSuccess('Đã duyệt GPLX');
      } catch (error) {
        ToastHelper.showError(getRentalErrorMessage(error));
      } finally {
        setActionLoading(false);
      }
    },
  });

  const submitReject = async () => {
    if (!reason.trim()) return;
    setActionLoading(true);
    try {
      const response = await rentalApi.rejectRenterLicense(rentalRenterLicenseId, reason.trim());
      setDetail(response?.license || null);
      setRejectOpen(false);
      setReason('');
      ToastHelper.showSuccess('Đã từ chối GPLX');
    } catch (error) {
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div>
      <div>
        {UIHelper.buildBreadcrumb([
          { title: 'GPLX khách thuê xe', onPress: () => router.navigate(-1) },
          { title: `Hồ sơ #${rentalRenterLicenseId}` },
        ])}
      </div>
      <div className="container-fluid px-4 my-4">
        <Card>
          <Spin spinning={loading}>
            {!detail && !loading ? <Empty description="Không tải được hồ sơ" /> : detail && (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <Title level={4} className="mb-0">Hồ sơ #{detail.rentalRenterLicenseId}</Title>
                  <LicenseStatusTag status={detail.status} />
                </div>

                {detail.duplicateWarning && (
                  <Alert
                    type="error"
                    showIcon
                    message="Phát hiện số GPLX trùng với hồ sơ khác"
                    description="Cần đối chiếu ảnh GPLX trước khi duyệt."
                  />
                )}

                <Descriptions title="Khách thuê" bordered column={1} size="small">
                  <Descriptions.Item label="Họ tên">{detail.user?.fullname || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Liên hệ">{detail.user?.phone || `User #${detail.userId}`}</Descriptions.Item>
                  <Descriptions.Item label="Số GPLX">{detail.licenseNumber || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Hạng GPLX">{detail.licenseClass || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Hết hạn">{formatDate(detail.licenseExpiryDate)}</Descriptions.Item>
                  <Descriptions.Item label="Nộp lúc">{formatDateTime(detail.createdAt)}</Descriptions.Item>
                </Descriptions>

                {detail.reviewReason && (
                  <Alert type="warning" showIcon message="Lý do xử lý gần nhất" description={detail.reviewReason} />
                )}

                {detail.status === 'PENDING' && (
                  canReview ? (
                    <Space wrap>
                      <Button type="primary" onClick={approve} loading={actionLoading}>Duyệt</Button>
                      <Button danger onClick={() => setRejectOpen(true)} loading={actionLoading}>Từ chối</Button>
                    </Space>
                  ) : <Alert type="info" showIcon message="Tài khoản chỉ có quyền xem, không có RENTAL_LICENSE_REVIEW." />
                )}
              </Space>
            )}
          </Spin>
        </Card>
      </div>

      <Modal
        title="Từ chối GPLX"
        open={rejectOpen}
        okText="Xác nhận từ chối"
        cancelText="Huỷ"
        confirmLoading={actionLoading}
        okButtonProps={{ danger: true, disabled: !reason.trim() }}
        onCancel={() => { setRejectOpen(false); setReason(''); }}
        onOk={submitReject}
      >
        <Input.TextArea
          rows={4}
          maxLength={1000}
          showCount
          value={reason}
          placeholder="Nêu rõ lý do từ chối"
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>
    </div>
  );
}

export default RentalRenterLicenseDetailScreen;
